import { ChangeEvent, FunctionComponent, useRef, useReducer } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Grid,
  MenuItem
} from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import i18n from 'i18n';
import CustomTextField from 'components/CustomTextField';
import SearchSelectUsers from 'components/SearchSelectUsers';
import { TaskStates } from 'pages/assignments/consts';
import { FormData } from 'pages/assignments/formData';
import DialogStories from 'pages/assignments/components/DialogStories';
import PriorityTag from 'pages/assignments/components/PriorityTag';
import {
  findTaskStateGuidByName,
  filterAvailableTaskStates,
  getTaskPriorityObject,
  useOpStates
} from 'pages/assignments/utils';
import {
  createInitialStates,
  reducer
} from 'pages/assignments/components/DialogForm/reducer';
import { combineFormData } from 'pages/assignments/components/DialogForm/utils';
import useStyles from 'pages/assignments/components/DialogForm/styles';
import Select from 'components/Select';

interface AssignmentEditFormI {
  isAdmin: boolean;
  currentLnsUser: $Lns.User;
  onSave: (formData: FormData) => void;
  onCancel: () => void;
  assignment: FormData;
  taskStates: $Lns.TaskState[];
}

const AssignmentEditForm: FunctionComponent<AssignmentEditFormI> = ({
  isAdmin,
  currentLnsUser,
  onSave,
  onCancel,
  assignment,
  taskStates
}: AssignmentEditFormI) => {
  const classes = useStyles();

  const {
    isCreation,
    isStateNew,
    canEditState,
    canEdit,
    canAccept,
    canReject
  } = useOpStates(assignment, currentLnsUser, isAdmin);

  // Managing Props Values and Existing Values
  const { assignedToObj: propsAssignedToObj, storyObj: propsStoryObj } =
    assignment;

  const { keys: taskPriorityKeys, values: taskPriorityValues } =
    getTaskPriorityObject();

  // Create the State Reducer
  const [states, dispatch] = useReducer(
    reducer,
    createInitialStates(assignment, currentLnsUser, isAdmin, taskStates)
  );

  // Decontruct quickhand access to the states
  const { editStory, formData, saveBtnDisabled } = states;
  const { assignedAtObj, dateDueObj, assignedByObj, priority } = formData;

  // Not All TaskStates are Available to the Select
  const selectableTaskStates = filterAvailableTaskStates(
    taskStates,
    formData,
    currentLnsUser,
    isAdmin
  );

  // ASSIGN_TO is a state managed in a sub component
  const refAssignedTo = useRef<$Lns.User | undefined>(propsAssignedToObj);

  // This is needed as the SearchSelectUser component expects an array of users
  const arrayedAssignedTo =
    refAssignedTo.current && Object.keys(refAssignedTo.current).length !== 0
      ? [refAssignedTo.current]
      : [];

  // Story associated with this assignment is managed in a sub component
  const refStoryObj = useRef<$Lns.Story | undefined>(propsStoryObj);
  const { current: storyObj } = refStoryObj;

  // A generalized function that deals with general inputs
  const onChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'onChangeHTMLInputElement',
      payload: e
    });
  };

  // A generalized function that deals with general selections
  const onChangeSelect = (key: string, value: number | string) => {
    dispatch({
      type: 'onChangeSelectElement',
      payload: { key, value }
    });
  };

  // Uses OnChangeSelect to handle selection of taskstate
  const handleTaskStateChange = (e: ChangeEvent<{ value: unknown }>) => {
    onChangeSelect('taskState', e.target.value as string);
  };

  // Uses OnChangeSelect to handle selection of priorities
  const handlePriorityChange = (e: ChangeEvent<{ value: unknown }>) => {
    onChangeSelect('priority', e.target.value as number);
  };

  // A generalized function that deals with Picking Date & TIme
  const onChangeDateTime = (key: string, value: Date) => {
    dispatch({
      type: 'onChangeDateTime',
      payload: { key, value }
    });
  };

  // STORY PICKER Component's handlers & callbacks
  const onEditStory = () => {
    // this brings up the edit story dialog
    dispatch({ type: 'editStory', payload: storyObj });
  };

  const onCancelEditStory = () => {
    dispatch({ type: 'editStory', payload: false });
  };

  const handleOnSaveStory = (newStory: $Lns.Story | undefined) => {
    if (
      newStory &&
      (!storyObj || (storyObj && newStory.guid !== storyObj.guid))
    ) {
      refStoryObj.current = newStory;
      toggleSaveButton();
    }
    onCancelEditStory();
  };
  // END - STORY PICKER Component's handlers & callbacks

  // Handler that deals with Search Select User Component
  const handleSelectedUsers = (newSelected: $Lns.User[]) => {
    let changedFlag = false;

    if (newSelected.length > 0) {
      if (refAssignedTo.current)
        changedFlag = refAssignedTo.current.guid !== newSelected[0].guid;
      else changedFlag = true;

      [refAssignedTo.current] = newSelected;
    } else {
      if (refAssignedTo.current) changedFlag = true;
      refAssignedTo.current = undefined;
    }

    // if there are changes, iow changedFlag is true,
    // then disableSaveButton should be true, and vice versa.
    forceSaveButton(!changedFlag);
  };

  // Functions to handle Save Button
  const toggleSaveButton = () => {
    dispatch({ type: 'toggleSaveButton' });
  };

  const forceSaveButton = (forceFlag: boolean) => {
    dispatch({
      type: 'forceSaveButton',
      payload: forceFlag
    });
  };
  // END Functions to handle Save Button

  // Save and close form
  const handleOnSaveForm = () => {
    let updatedFormData: FormData = formData;

    if ((isCreation || isStateNew) && refAssignedTo.current) {
      const pendingStateGuid = findTaskStateGuidByName(
        TaskStates.PENDING_ASSIGNMENT,
        taskStates
      );

      updatedFormData = {
        ...formData,
        taskState: pendingStateGuid || ''
      };
    }

    if (propsAssignedToObj && !refAssignedTo.current) {
      const newStateGuid = findTaskStateGuidByName(TaskStates.NEW, taskStates);
      updatedFormData = {
        ...formData,
        taskState: newStateGuid || ''
      };
    }

    onSave(
      combineFormData(
        updatedFormData,
        refStoryObj.current,
        assignedByObj,
        assignedAtObj,
        refAssignedTo.current,
        dateDueObj
      )
    );
  };

  // Accept and Save Form
  const handleOnAccept = () => {
    const acceptStateGuid = findTaskStateGuidByName(
      TaskStates.ASSIGNED,
      taskStates
    );

    const updatedFormData: FormData = {
      ...formData,
      taskState: acceptStateGuid || ''
    };

    onSave(
      combineFormData(
        updatedFormData,
        refStoryObj.current,
        assignedByObj,
        assignedAtObj,
        currentLnsUser, // assign current user to Assigned_To
        dateDueObj
      )
    );
  };

  // Reject and Save Form
  const handleOnReject = () => {
    const rejectStateGuid = findTaskStateGuidByName(
      TaskStates.REJECTED,
      taskStates
    );

    const updatedFormData: FormData = {
      ...formData,
      taskState: rejectStateGuid || ''
    };

    onSave(
      combineFormData(
        updatedFormData,
        refStoryObj.current,
        assignedByObj,
        assignedAtObj,
        refAssignedTo.current,
        dateDueObj
      )
    );
  };

  return (
    <>
      <Dialog onClose={onCancel} aria-labelledby="edit-assignment" open>
        <Box display="flex" justifyContent="start" gridGap={16}>
          <DialogTitle
            id="edit-assignment-title"
            className={classes.dialogTitle}
          >
            {isCreation
              ? i18n.t('pages.assignments.dialogCreate.title')
              : i18n.t('pages.assignments.dialogEdit.title')}
          </DialogTitle>
          <Box className={classes.flexRight}>
            <Select
              labelId="select-priority-label"
              id="select-priority"
              value={priority}
              onChange={handlePriorityChange}
              label={i18n.t('pages.assignments.formLabel.priority')}
            >
              {taskPriorityKeys.map((taskKey, index) => {
                return (
                  <MenuItem
                    key={taskPriorityValues[index]}
                    value={taskPriorityValues[index]}
                  >
                    <PriorityTag priority={taskKey} variant="text" />
                  </MenuItem>
                );
              })}
            </Select>
            {!isCreation && (
              <Select
                labelId="select-taskstate-label"
                id="select-taskstate"
                value={formData.taskState}
                onChange={handleTaskStateChange}
                label={i18n.t('pages.assignments.formLabel.taskStates')}
              >
                {selectableTaskStates.map(stateItem => (
                  <MenuItem key={stateItem.guid} value={stateItem.guid}>
                    {stateItem.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomTextField
              onChange={onChangeField}
              value={formData.title}
              label={i18n.t('pages.assignments.formLabel.title')}
              name="title"
              error={!!formData?.errors.title}
              required
              helperText={formData.errors.title}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              rows={3}
              onChange={onChangeField}
              value={formData.description}
              label={i18n.t('pages.assignments.formLabel.description')}
              name="description"
              error={!!formData?.errors.description}
              required
              helperText={formData.errors.description}
              disabled={!canEdit}
            />
          </Grid>
          {isAdmin && ( // Only Visible and Editable by Admins
            <Grid item xs={12}>
              <DateTimePicker
                fullWidth
                autoOk
                variant="inline"
                name="assignedAt"
                inputVariant="outlined"
                label={i18n.t('pages.assignments.formLabel.assignedAt')}
                format="MM/dd/yyyy hh:mm"
                value={assignedAtObj}
                onChange={date =>
                  onChangeDateTime('assignedAtObj', date as Date)
                }
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <DateTimePicker
              fullWidth
              autoOk
              variant="inline"
              name="dateDue"
              inputVariant="outlined"
              label={i18n.t('pages.assignments.formLabel.dateDue')}
              format="MM/dd/yyyy hh:mm"
              value={dateDueObj}
              onChange={date => onChangeDateTime('dateDueObj', date as Date)}
              minDate={assignedAtObj || new Date()}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="flex-end" gridGap={16}>
              <Box flex={1}>
                <CustomTextField
                  disabled
                  margin="none"
                  onChange={() => {}}
                  value={storyObj ? storyObj.slug : 'No Story Selected'}
                  label={i18n.t('pages.assignments.formLabel.storySlug')}
                  name="story-slug"
                  error={false}
                />
              </Box>
              <Button
                className={classes.storyButton}
                variant="contained"
                onClick={onEditStory}
                disabled={!canEdit}
              >
                ...
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <SearchSelectUsers
              title={i18n.t('pages.assignments.formLabel.assignedTo')}
              placeholderText={i18n.t(
                'pages.assignments.placeholderText.assignedTo'
              )}
              maxSelect={1}
              selectedUserList={arrayedAssignedTo}
              onChangeSelect={handleSelectedUsers}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" width="100%">
              <Button color="primary" variant="outlined" onClick={onCancel}>
                {i18n.t('button.cancel')}
              </Button>
              {(isAdmin || canEdit || canEditState) && (
                <Box paddingLeft={2}>
                  <Button
                    disabled={saveBtnDisabled}
                    color="primary"
                    variant="contained"
                    onClick={handleOnSaveForm}
                  >
                    {i18n.t('button.save')}
                  </Button>
                </Box>
              )}
              {canAccept && (
                <Box paddingLeft={2}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleOnAccept}
                  >
                    {i18n.t('pages.assignments.button.accept')}
                  </Button>
                </Box>
              )}
              {canReject && (
                <Box paddingLeft={2}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleOnReject}
                  >
                    {i18n.t('pages.assignments.button.reject')}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Dialog>
      {editStory !== false && (
        <DialogStories
          selectedStory={editStory}
          onSave={handleOnSaveStory}
          onCancel={onCancelEditStory}
        />
      )}
    </>
  );
};

export default AssignmentEditForm;
